import type { ReactNode } from 'react'
import { Cell, Pie, PieChart } from 'recharts'

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/utils/cn'

export type DonutDatum = { segment: string; value: number; fill: string }

/** Grid used by the overview stat rows on the record group pages. */
export const STAT_GRID_CLASS = 'grid gap-3 sm:grid-cols-2 xl:grid-cols-4'

export function DonutChart({ data, config, center, caption }: {
  data: DonutDatum[]
  config: ChartConfig
  center: string
  caption?: string
}) {
  // An all-zero pie renders nothing; show an empty ring instead.
  const empty = data.every((d) => d.value === 0)
  const chartData = empty ? [{ segment: 'empty', value: 1, fill: 'var(--color-muted, #e5e7eb)' }] : data
  return (
    <div className="relative h-24 w-24 shrink-0">
      <ChartContainer config={config} className="aspect-square h-full w-full">
        <PieChart>
          <Pie data={chartData} dataKey="value" nameKey="segment" innerRadius={34} outerRadius={47} strokeWidth={0} isAnimationActive={false}>
            {chartData.map((entry) => <Cell key={entry.segment} fill={entry.fill} />)}
          </Pie>
          {!empty && (
            <ChartTooltip
              allowEscapeViewBox={{ x: true, y: true }}
              wrapperStyle={{ zIndex: 50 }}
              content={<ChartTooltipContent nameKey="segment" className="relative z-50 bg-background opacity-100" />}
            />
          )}
        </PieChart>
      </ChartContainer>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-lg font-bold tabular-nums text-foreground">{center}</span>
        {caption && <span className="text-[10px] text-muted-foreground">{caption}</span>}
      </div>
    </div>
  )
}

export function StatCard({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-w-0 min-h-36 items-center justify-between gap-3 rounded-xl border bg-background p-4">
      {children}
    </div>
  )
}

/** Title, headline value and caption on the left of a stat card. */
export function StatText({ title, value, caption }: { title: string; value: ReactNode; caption?: ReactNode }) {
  return (
    <div className="min-w-0">
      <h3 className="text-sm font-medium text-muted-foreground">{title}</h3>
      <p className="mt-1 text-2xl font-bold tabular-nums text-primary">{value}</p>
      {caption && <p className="mt-1 text-xs text-muted-foreground">{caption}</p>}
    </div>
  )
}

/** Small colored legend rows (label + count) for cards that list segments. */
export function StatLegend({ items }: { items: { label: string; value: number; color: string }[] }) {
  return (
    <div className="mt-2 space-y-1.5 text-xs">
      {items.map((item) => (
        <div key={item.label} className="flex items-center gap-2">
          <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
          <span className="truncate text-muted-foreground">{item.label}</span>
          <span className="ml-auto font-medium tabular-nums">{item.value}</span>
        </div>
      ))}
    </div>
  )
}

export function StatCardsSkeleton({ label, count = 4, className }: { label: string; count?: number; className?: string }) {
  return (
    <section aria-label={label} className={cn(STAT_GRID_CLASS, className)}>
      {Array.from({ length: count }, (_, item) => (
        <StatCard key={item}>
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-16" />
            <Skeleton className="h-3 w-28" />
          </div>
          <Skeleton className="h-24 w-24 shrink-0 rounded-full" />
        </StatCard>
      ))}
    </section>
  )
}
